import { Component, HostListener, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet
} from '@angular/router';
import { distinctUntilChanged, filter, map, tap } from 'rxjs';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { BaseComponent } from './~shared/abstract-components/base.component';
import { FaConfig, FaIconLibrary } from '@fortawesome/angular-fontawesome';
import {
  faPen,
  faFileArrowUp,
  faXmark,
  faSpinnerThird,
  faTrash
} from '@fortawesome/pro-duotone-svg-icons';
import {
  faInfo as fasInfo,
  faFilePlus as fasFilePlus,
  faFileCertificate as fasFileCertificate,
  faArrowsRotate as fasArrowsRotate,
  faChevronLeft as fasChevronLeft
} from '@fortawesome/pro-solid-svg-icons';
import {
  faTrashCan as falTrashCan,
  faPenToSquare as falPenToSquare,
  faFileArrowUp as falFileArrowUp,
  faMagnifyingGlass as falMagnifyingGlass,
  faDownload as falDownload,
  faUserPlus as falUserPlus,
  faUserMinus as falUserMinus,
  faLock,
  faUnlock,
  faEye
} from '@fortawesome/pro-light-svg-icons';
import {
  faEllipsisVertical as farEllipsisVertical,
  faXmark as farXmark
} from '@fortawesome/pro-regular-svg-icons';
import { initFlowbite } from 'flowbite';
import { NgSelectConfig } from '@ng-select/ng-select';
import { NgxPermissionsService } from 'ngx-permissions';
import { AuthFacade } from './~store/facades/auth.facade';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [LoadingBarModule, LoadingBarHttpClientModule, RouterOutlet]
})
export class AppComponent extends BaseComponent implements OnInit {
  @HostListener('window:online', ['$event'])
  onOnlineEvent(): void {
    this.setOnlineStatus(true);
  }

  @HostListener('window:offline', ['$event'])
  onOfflineEvent(): void {
    this.setOnlineStatus(false);
  }

  private _ngSelectConfig = inject(NgSelectConfig);
  private _authFacade = inject(AuthFacade);
  private _permissionsService = inject(NgxPermissionsService);

  constructor(
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _faIconLibrary: FaIconLibrary,
    private _faConfig: FaConfig
  ) {
    super();
    this._faIconLibrary.addIcons(
      fasInfo,
      fasFilePlus,
      fasFileCertificate,
      faXmark,
      farXmark,
      farEllipsisVertical,
      faPen,
      faFileArrowUp,
      faSpinnerThird,
      faTrash,
      fasChevronLeft,
      fasArrowsRotate,
      falTrashCan,
      falPenToSquare,
      falFileArrowUp,
      falMagnifyingGlass,
      falDownload,
      faLock,
      faUnlock,
      falUserPlus,
      falUserMinus,
      faEye
    );
    this._faConfig.defaultPrefix = 'fad';
  }

  ngOnInit() {
    if (this._isPlatformBrowser) {
      this.setOnlineStatus();
      this.listenToRouteParamsChanges();
      initFlowbite();
    }
    this.initNgSelectConfig();
    this.listenToRoleChanges();
  }

  initNgSelectConfig(): void {
    this._ngSelectConfig.notFoundText = 'Aucun élément trouvé';
  }

  listenToRouteParamsChanges(): void {
    this._router.events
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter(event => event instanceof NavigationEnd),
        map(() => this._activatedRoute),
        map(route => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        tap(({ snapshot }) => {
          const routeData = this._globalFacade.routeData;
          this._globalFacade.setRouteData({
            params: snapshot.params,
            queryParams: snapshot.queryParams,
            currentUrl: this._router.url,
            currentUrlWithoutParams: this._router.url.split('?')[0],
            previousUrl: routeData?.currentUrl || '/',
            data: snapshot.data,
            state: this._router.getCurrentNavigation()?.extras.state
          });
        })
      )
      .subscribe();
  }

  listenToRoleChanges(): void {
    this._authFacade.currentUser$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        map(user => user?.roles),
        distinctUntilChanged(),
        tap({
          next: roles => {
            this._permissionsService.loadPermissions(roles || []);
          }
        })
      )
      .subscribe();
  }

  setOnlineStatus(status?: boolean): void {
    this._globalFacade.setOnlineStatus(
      status === undefined ? navigator.onLine : status
    );
  }
}
